import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { closestCenter, DndContext } from "@dnd-kit/core";

function SortableItem({ item }) {
  const id = String(item.id);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const transformStr =
    transform && CSS.Transform && typeof CSS.Transform.toString === "function"
      ? CSS.Transform.toString(transform)
      : undefined;

  const style = {
    transform: transformStr,
    transition,
    zIndex: isDragging ? 999 : "auto",
    cursor: "grab",
    marginBottom: 8,
  };

  return (
    <ListItem
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      component={Paper}
      style={style}
    >
      <ListItemText primary={item.title} />
    </ListItem>
  );
}

export default function ReorderDialog({
  open,
  onClose,
  highPriorityAnnouncements,
  onReorderComplete,
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!Array.isArray(highPriorityAnnouncements)) return;
    const reordered = highPriorityAnnouncements
      .filter(Boolean)
      .map((a) => ({
        ...a,
        id: String(a.id ?? a._id ?? a.key ?? a),
      }))
      .filter((a) => a.id && a.id !== "undefined");
    setItems(reordered);
  }, [highPriorityAnnouncements]);

  const handleDragEnd = (event) => {
    const activeId = event?.active?.id;
    const overId = event?.over?.id;

    if (!activeId || !overId || String(activeId) === String(overId)) {
      return;
    }

    setItems((prev) => {
      const oldIndex = prev.findIndex(
        (it) => String(it.id) === String(activeId),
      );
      const newIndex = prev.findIndex((it) => String(it.id) === String(overId));

      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    try {
      const endpoint = "/admin/announcements/reorder.json";
      const payload = {
        ids: items.map((i) => Number(i.id)),
      };

      const res = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          `Failed: ${res.status} ${res.statusText}, response: ${JSON.stringify(data)}`,
        );
      }

      onReorderComplete?.(items);
      onClose();
    } catch (err) {}
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reorder High Priority Announcements</DialogTitle>
      <DialogContent>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => String(i.id))}
            strategy={verticalListSortingStrategy}
          >
            <List>
              {items.map((item) => (
                <SortableItem key={String(item.id)} item={item} />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
